import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { validateCreatorFile } from '../utils/mediaValidation';

const UploadBox = ({ label, title, value, onChange, accept, onSubjectAdd }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const fileInputRef = useRef(null);
  const menuRef = useRef(null);

  // Close menu on outside click
  useEffect(() => {
    if (!showMenu) return;
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const handleClick = () => {
    if (uploading) return;
    if (value) {
      // Already has content — show menu to replace or remove
      setShowMenu((prev) => !prev);
    } else {
      setShowMenu((prev) => !prev);
    }
  };

  const handleUrlInput = () => {
    setShowMenu(false);
    const url = window.prompt(`输入${label} URL`);
    if (url) {
      onChange(url);
      if (onSubjectAdd) onSubjectAdd(url, null);
    }
  };

  const handleFileSelect = () => {
    setShowMenu(false);
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset input so same file can be re-selected
    e.target.value = '';

    try {
      await validateCreatorFile(file, { scope: 'image-only' });
    } catch (error) {
      alert(error.message);
      return;
    }

    setUploading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post('/api/creator/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) {
            setProgress(Math.round((evt.loaded / evt.total) * 100));
          }
        },
      });

      if (res.data.success) {
        const url = res.data.data.url;
        onChange(url);
        if (onSubjectAdd) onSubjectAdd(url, file);
      } else {
        alert('上传失败: ' + (res.data.message || '未知错误'));
      }
    } catch (err) {
      alert('上传失败: ' + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
      setProgress(0);
    }
  };

  const handleRemove = (e) => {
    e.stopPropagation();
    setShowMenu(false);
    onChange('');
  };

  const isVideo = value && /\.(mp4|webm|mov)(\?|$)/i.test(value);

  return (
    <div className="creator-frame-box" title={title} ref={menuRef} onClick={handleClick}>
      {uploading ? (
        <div className="creator-upload-progress">
          <div className="creator-upload-progress-bar">
            <div
              className="creator-upload-progress-fill"
              style={{ width: `${progress}%` }}
            />
          </div>
          <span className="creator-upload-progress-text">{progress}%</span>
        </div>
      ) : value ? (
        <>
          {isVideo ? (
            <video src={value} muted />
          ) : (
            <img src={value} alt={label} />
          )}
          <button
            className="creator-frame-box-remove"
            onClick={handleRemove}
            title="移除"
          >
            &times;
          </button>
        </>
      ) : (
        <>
          <span className="creator-frame-box-plus">+</span>
          <span className="creator-frame-box-label">{label}</span>
        </>
      )}

      {showMenu && !uploading && (
        <div className="creator-upload-menu" onClick={(e) => e.stopPropagation()}>
          <div className="creator-upload-menu-item" onClick={handleFileSelect}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
            <span>本地文件</span>
          </div>
          <div className="creator-upload-menu-item" onClick={handleUrlInput}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
              <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
            </svg>
            <span>输入 URL</span>
          </div>
          {value && (
            <div className="creator-upload-menu-item danger" onClick={handleRemove}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              <span>移除</span>
            </div>
          )}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept={accept || 'image/*,video/mp4,video/webm,video/quicktime'}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default UploadBox;
